import { useEffect, useRef, useState } from "preact/hooks";
import { offsetFor, progressOf, stepFor } from "./motion.js";

const IDLE_AFTER = 3000;

export function isPinned(top: number, height: number, viewport: number): boolean {
  return top <= viewport * 0.15 && top + height >= viewport * 0.85;
}

export function useScrollSteps(total: number) {
  const track = useRef<HTMLDivElement>(null);
  const [live, setLive] = useState(false);
  const [step, setStep] = useState(0);
  const [idle, setIdle] = useState(false);

  useEffect(() => {
    setLive(true);
    let queued = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const read = () => {
      queued = false;
      const element = track.current;
      if (!element) {
        return;
      }
      const box = element.getBoundingClientRect();
      setStep(stepFor(progressOf(box.top, box.height, window.innerHeight), total));
      setIdle(false);
      clearTimeout(timer);
      if (isPinned(box.top, box.height, window.innerHeight)) {
        timer = setTimeout(() => setIdle(true), IDLE_AFTER);
      }
    };
    const schedule = () => {
      if (!queued) {
        queued = true;
        requestAnimationFrame(read);
      }
    };
    read();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [total]);

  const go = (target: number) => {
    const element = track.current;
    if (!element) {
      setStep(target);
      return;
    }
    const box = element.getBoundingClientRect();
    const distance = box.height - window.innerHeight;
    window.scrollTo({
      top: window.scrollY + box.top + offsetFor(target, total, distance),
      behavior: "smooth",
    });
  };

  const next = () => go(Math.min(step + 1, total - 1));

  return { track, live, step, idle, go, next };
}

export function pad(value: number): string {
  return String(value).padStart(2, "0");
}
